// Hotfix-B (Part A) — pure duplicate analysis for the "Database cleanup" modal.
//
// Two rows are duplicates iff normalized NAME matches AND normalized EMAIL
// matches (both trim + lowercase). Name basis mirrors CollectiveCaseView dupKey:
// companyName for companies, last+first for individuals. A blank email never
// participates (a blank never matches another blank), so blank-email rows stay
// neutral orphans regardless of name. Within a duplicate group the lowest-id row
// SURVIVES (kept, left unchecked); the rest are flagged + pre-checked for delete.
//
// Authored as ESM `.mjs` (same rationale as lib/collectiveSegments.mjs) so it
// loads unchanged in BOTH the Vite frontend AND a plain `node` smoke harness —
// keeping the survivor/pre-check rule testable without a browser. The server
// mirrors this exact rule in routes/clients.js deletionBoundDuplicateIds so the
// modal's pre-check and the backfill's dup-skip always agree.
//
// Pure: no React, no DOM, no network.

export function orphanNameKey(c) {
  return (c.type || "individual") === "company"
    ? "co::" + (c.companyName || "").trim().toLowerCase()
    : "in::" +
        (c.lastName || "").trim().toLowerCase() +
        "||" +
        (c.firstName || "").trim().toLowerCase();
}

// Returns { dupIds, survivorIds, preChecked } as Sets of client id:
//   dupIds      — every member of a (name+email) group of size >= 2 (red flag)
//   survivorIds — the one kept copy per group (lowest id; left unchecked)
//   preChecked  — non-survivor duplicates (checkbox pre-checked for deletion)
export function analyzeOrphanClients(list) {
  const groups = new Map();
  for (const c of list || []) {
    const email = (c.email || "").trim().toLowerCase();
    if (!email) continue; // blank email never participates in dup detection
    const key = orphanNameKey(c) + "@@" + email;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(c.id);
  }
  const dupIds = new Set();
  const survivorIds = new Set();
  const preChecked = new Set();
  for (const ids of groups.values()) {
    if (ids.length < 2) continue;
    const sorted = ids.slice().sort((a, b) => a - b);
    survivorIds.add(sorted[0]); // survivor = lowest id
    sorted.forEach((id, i) => {
      dupIds.add(id);
      if (i > 0) preChecked.add(id); // everyone except the survivor
    });
  }
  return { dupIds, survivorIds, preChecked };
}
